/**
 * ConversationCostTimeline
 *
 * Per-conversation cost view that answers the question: "Why did this
 * turn cost what it cost?". Renders each assistant turn as a row with:
 *   - Provider/model badge (color-coded)
 *   - Cache state badge (cold start / hit / lost via switch / warming /
 *     expired / rewrite / no cache)
 *   - Model-switch markers (red banner between turns when provider/model
 *     changed)
 *   - Per-turn tokens + cost
 *   - Click-to-expand: per-iteration drilldown
 *
 * Goal: when you see a $0.07 turn next to a $0.005 turn, the cache state
 * + switch marker explain WHY without you needing to dig into the events.
 */

import { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import {
  Card,
  Col,
  Empty,
  Row,
  Select,
  Spin,
  Statistic,
  Tag,
  Tooltip,
  Typography,
  theme,
  message as antdMessage,
} from 'antd';
import {
  ApiOutlined,
  CaretRightOutlined,
  CaretDownOutlined,
  DatabaseOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import insightsService, {
  CacheStateLabel,
  ConversationSummaryResponse,
  ConversationTimelineResponse,
  ConversationTurn,
  ConversationTurnEvent,
  RecentConversation,
} from '../services/insightsService';

dayjs.extend(relativeTime);

const { Text } = Typography;

interface ConversationCostTimelineProps {
  /** Optional pre-selected conversation. If unset, shows the picker. */
  initialConversationId?: string;
  tenantId?: string;
  /** Narrows the picker to conversations from these agents only. */
  chatbotIds?: string[];
  /** Date window from the parent Cost Breakdown filter row. The recent-
   *  conversations picker MUST refresh when either bound changes — without
   *  these props it stayed stuck on conversations from any time, including
   *  ones with zero ledger activity in the user's selected window. */
  dateFrom?: string;
  dateTo?: string;
}

const PROVIDER_COLORS: Record<string, string> = {
  anthropic: '#d97757',
  openai: '#10a37f',
  google: '#4285f4',
  moonshot: '#7c3aed',
};

const CACHE_STATE_META: Record<Exclude<CacheStateLabel, null>, {
  color: string;
  icon: React.ReactNode;
  label: string;
}> = {
  cold_start: { color: 'default', icon: <ThunderboltOutlined />, label: 'cold start' },
  cache_hit: { color: 'success', icon: <CheckCircleOutlined />, label: 'cache hit' },
  cache_lost_switch: { color: 'error', icon: <WarningOutlined />, label: 'cache lost — model switch' },
  cache_warming: { color: 'gold', icon: <DatabaseOutlined />, label: 'cache warming' },
  cache_rewrite: { color: 'orange', icon: <DatabaseOutlined />, label: 'cache rewrite' },
  cache_expired: { color: 'volcano', icon: <CloseCircleOutlined />, label: 'cache expired (TTL)' },
  no_cache: { color: 'default', icon: <ApiOutlined />, label: 'no cache' },
};

function fmtUsd(n: number): string {
  if (n >= 1) return `$${n.toFixed(2)}`;
  if (n > 0) return `$${n.toFixed(6)}`;
  return '$0';
}

function fmtN(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString('en-US');
}

function modelLabel(model: string | null): string {
  if (!model) return '—';
  return model.length > 28 ? `${model.slice(0, 28)}…` : model;
}

export function ConversationCostTimeline({
  initialConversationId,
  tenantId,
  chatbotIds,
  dateFrom,
  dateTo,
}: ConversationCostTimelineProps) {
  const { t } = useLanguage();
  const [recent, setRecent] = useState<RecentConversation[]>([]);
  const [selected, setSelected] = useState<string | null>(initialConversationId ?? null);
  const [timeline, setTimeline] = useState<ConversationTimelineResponse | null>(null);
  const [summary, setSummary] = useState<ConversationSummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);

  // Load recent conversations for the picker — narrowed by tenant/agent
  // AND the active date window from the Cost Breakdown filter row. The
  // date filter is the one the user just changed; without it in the
  // dep array, this picker silently kept showing conversations outside
  // the selected window.
  useEffect(() => {
    let cancelled = false;
    insightsService
      .getRecentConversations({ tenantId, chatbotIds, dateFrom, dateTo })
      .then(r => { if (!cancelled) setRecent(r.conversations ?? []); })
      .catch(() => { /* picker can be empty */ });
    return () => { cancelled = true; };
  }, [tenantId, chatbotIds?.join(','), dateFrom, dateTo]);

  // Load timeline + summary when selection changes
  useEffect(() => {
    if (!selected) {
      setTimeline(null);
      setSummary(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      insightsService.getConversationTimeline(selected, { tenantId }),
      insightsService.getConversationSummary(selected, { tenantId }),
    ])
      .then(([tl, sm]) => {
        if (cancelled) return;
        setTimeline(tl);
        setSummary(sm);
      })
      .catch(err => {
        antdMessage.error(err?.response?.data?.error ?? t('insights.timeline.loadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [selected, tenantId]);

  const pickerOptions = useMemo(() => recent.map(c => ({
    value: c.conversation_id,
    label: (
      <span>
        <strong dir="auto">{c.conversation_title || '(untitled)'}</strong>
        {c.chatbot_name && <Text type="secondary"> · {c.chatbot_name}</Text>}
        {' · '}
        <Text type="secondary">{t('insights.costTimeline.costAndCalls', { cost: fmtUsd(c.total_cost_usd), count: c.event_count })}</Text>
        {c.created_at && <Text type="secondary"> · {dayjs(c.created_at).fromNow()}</Text>}
      </span>
    ),
  })), [recent]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Text strong>Conversation:</Text>
          <Select
            showSearch
            placeholder={t('insights.costTimeline.pickConversation')}
            style={{ minWidth: 480, flex: 1 }}
            options={pickerOptions}
            value={selected}
            onChange={setSelected}
            optionFilterProp="label"
            filterOption={(input, option) =>
              JSON.stringify(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            allowClear
          />
          {recent.length === 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('insights.noConversationsWithCostEvents')}
            </Text>
          )}
        </div>
      </Card>

      {loading && (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin />
        </div>
      )}

      {selected && !loading && summary && (
        <ConversationSummaryCard summary={summary} />
      )}

      {selected && !loading && timeline && timeline.turns.length === 0 && (
        <Empty
          description={
            <span>{t('insights.timeline.noEventsInLedger')}</span>
          }
          style={{ padding: 60 }}
        />
      )}

      {selected && !loading && timeline && timeline.turns.length > 0 && (
        <Card title={t('insights.costTimeline.turnByTurn')} bodyStyle={{ padding: 0 }}>
          {timeline.turns.map((turn, idx) => (
            <TurnRow
              key={turn.message_id}
              turn={turn}
              index={idx}
            />
          ))}
        </Card>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Summary card — the headline numbers for the picked conversation
// ─────────────────────────────────────────────────────────────────────────
function ConversationSummaryCard({ summary }: { summary: ConversationSummaryResponse }) {
  const { t } = useLanguage();
  const { token } = theme.useToken();
  const elapsedMs = summary.first_event_at && summary.last_event_at
    ? dayjs(summary.last_event_at).diff(dayjs(summary.first_event_at))
    : 0;

  // Headline number: total tokens that hit the provider's bill (every billable
  // category). Breakdown row below splits it. Cache reads are billed at a
  // discount, cache writes at a premium — they're still billed.
  const totalBillableTokens =
    summary.input_tokens
    + summary.cache_read_tokens
    + summary.cache_creation_tokens
    + summary.output_tokens;

  return (
    <Card>
      {/* Row 1 — the headlines */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title={t('insights.costTimeline.totalCost')}
            value={summary.total_cost_usd}
            precision={6}
            prefix="$"
            valueStyle={{ color: token.colorPrimary, fontSize: 28 }}
          />
          <Text type="secondary" style={{ fontSize: 11 }}>
            {summary.call_count} provider calls
          </Text>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title={t('insights.costTimeline.totalTokens')}
            value={fmtN(totalBillableTokens)}
            valueStyle={{ fontSize: 28 }}
          />
          <Text type="secondary" style={{ fontSize: 11 }}>
            input + cache_read + cache_write + output
          </Text>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title={t('insights.costTimeline.cacheSavings')}
            value={summary.cache_savings_pct.toFixed(1)}
            suffix="%"
            valueStyle={{
              fontSize: 28,
              color: summary.cache_savings_pct > 60 ? '#10b981'
                : summary.cache_savings_pct > 20 ? '#f59e0b'
                : '#94a3b8',
            }}
          />
          <Text type="secondary" style={{ fontSize: 11 }}>
            of input served from cache (~10% rate)
          </Text>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Tooltip title={t('insights.costTimeline.modelSwitchesTip')}>
            <Statistic
              title={t('insights.costTimeline.modelSwitches')}
              value={summary.model_switch_count}
              valueStyle={{
                fontSize: 28,
                color: summary.model_switch_count > 0 ? '#ef4444' : undefined,
              }}
            />
          </Tooltip>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {summary.model_switch_count > 0
              ? 'each one resets the cache'
              : 'stayed on one model'}
          </Text>
        </Col>
      </Row>

      {/* Row 2 — the full token breakdown that produces the bill */}
      <div style={{
        marginTop: 16,
        padding: 12,
        borderRadius: 6,
        background: token.colorBgLayout,
      }}>
        <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {t('insights.tokenBreakdownTheNumbersThat')}
        </Text>
        <Row gutter={16} style={{ marginTop: 6 }}>
          <Col xs={12} sm={8} md={4}>
            <Statistic
              title={t('insights.costTimeline.inputUncached')}
              value={fmtN(summary.input_tokens)}
              valueStyle={{ fontSize: 18 }}
            />
            <Text type="secondary" style={{ fontSize: 10 }}>{t('insights.timeline.fullProviderRate')}</Text>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Statistic
              title={t('insights.costTimeline.cacheReads')}
              value={fmtN(summary.cache_read_tokens)}
              valueStyle={{
                fontSize: 18,
                color: summary.cache_read_tokens > 0 ? '#10b981' : undefined,
              }}
            />
            <Text type="secondary" style={{ fontSize: 10 }}>{t('insights.timeline.tenPercentOfInput')}</Text>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Statistic
              title={t('insights.costTimeline.cacheWrites')}
              value={fmtN(summary.cache_creation_tokens)}
              valueStyle={{
                fontSize: 18,
                color: summary.cache_creation_tokens > 0 ? '#f59e0b' : undefined,
              }}
            />
            <Text type="secondary" style={{ fontSize: 10 }}>{t('insights.timeline.cacheWriteRate')}</Text>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Statistic
              title={t('insights.costTimeline.output')}
              value={fmtN(summary.output_tokens)}
              valueStyle={{ fontSize: 18 }}
            />
            <Text type="secondary" style={{ fontSize: 10 }}>{t('insights.timeline.fullOutputRate')}</Text>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Statistic
              title={t('insights.costTimeline.reasoning')}
              value={fmtN(summary.reasoning_tokens)}
              valueStyle={{
                fontSize: 18,
                color: summary.reasoning_tokens > 0 ? '#a855f7' : undefined,
              }}
            />
            <Text type="secondary" style={{ fontSize: 10 }}>{t('insights.timeline.reasoningSubset')}</Text>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Statistic
              title={t('insights.costTimeline.duration')}
              value={elapsedMs > 0 ? dayjs(summary.last_event_at).from(summary.first_event_at, true) : '—'}
              valueStyle={{ fontSize: 18 }}
            />
            <Text type="secondary" style={{ fontSize: 10 }}>{t('insights.timeline.firstToLastEvent')}</Text>
          </Col>
        </Row>
      </div>

      {summary.models_used.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>{t('insights.timeline.modelsUsed')} </Text>
          {summary.models_used.map(m => (
            <Tag
              key={`${m.provider}/${m.model}`}
              color={PROVIDER_COLORS[m.provider] ?? 'default'}
            >
              {m.provider}/{m.model}
            </Tag>
          ))}
        </div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// One turn row — the visual heart of the timeline
// ─────────────────────────────────────────────────────────────────────────
function TurnRow({ turn, index }: { turn: ConversationTurn; index: number }) {
  const { t } = useLanguage();
  const { token } = theme.useToken();
  const [expanded, setExpanded] = useState(false);
  const cacheMeta = turn.cache_state ? CACHE_STATE_META[turn.cache_state] : null;
  const providerColor = turn.provider ? PROVIDER_COLORS[turn.provider] : undefined;
  const isSwitch = turn.cache_state === 'cache_lost_switch';

  return (
    <>
      {/* Switch banner between turns */}
      {isSwitch && (
        <div
          style={{
            padding: '8px 16px',
            background: '#fff1f0',
            borderInlineStart: '4px solid #ef4444',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
          }}
        >
          <WarningOutlined style={{ color: '#ef4444' }} />
          <Text strong style={{ color: '#991b1b' }}>{t('agentDebug.modelSwitched')}</Text>
          <Tag>{turn.switched_from || 'previous'}</Tag>
          <span>→</span>
          <Tag color={providerColor}>{turn.switched_to || turn.model_name}</Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>
            previous provider's cache does not transfer
          </Text>
        </div>
      )}

      <div
        style={{
          padding: '12px 16px',
          borderTop: index === 0 ? 'none' : `1px solid ${token.colorBorderSecondary}`,
          background: isSwitch ? '#fff5f5' : undefined,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(e => !e)}
      >
        {/* Turn number + expand toggle */}
        <div style={{ minWidth: 32, textAlign: 'center' }}>
          {expanded ? <CaretDownOutlined /> : <CaretRightOutlined className="rtl-flip" />}
          <div style={{ fontSize: 11, color: token.colorTextSecondary }}>#{index + 1}</div>
        </div>

        {/* Provider + cache state */}
        <div style={{ minWidth: 240, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Tag color={providerColor} style={{ fontWeight: 600 }}>
            {turn.provider}/{modelLabel(turn.model_name)}
          </Tag>
          {cacheMeta && (
            <Tooltip title={turn.cache_explanation}>
              <Tag color={cacheMeta.color} icon={cacheMeta.icon} style={{ fontSize: 11 }}>
                {cacheMeta.label}
              </Tag>
            </Tooltip>
          )}
        </div>

        {/* Token breakdown */}
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, fontSize: 12 }}>
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>{t('agentDebug.titleUncachedInput')}</Text>
            <div>{fmtN(turn.input_tokens)}</div>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>{t('agentDebug.titleCacheRead')}</Text>
            <div style={{ color: turn.cache_read_tokens > 0 ? '#10b981' : undefined }}>
              {fmtN(turn.cache_read_tokens)}
            </div>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>{t('agentDebug.titleCacheWrite')}</Text>
            <div style={{ color: turn.cache_creation_tokens > 0 ? '#f59e0b' : undefined }}>
              {fmtN(turn.cache_creation_tokens)}
            </div>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {turn.reasoning_tokens > 0 ? t('insights.costTimeline.outputWithReasoning', { count: fmtN(turn.reasoning_tokens) }) : t('insights.costTimeline.outputPlain')}
            </Text>
            <div>{fmtN(turn.output_tokens)}</div>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>iterations · tools</Text>
            <div>
              {turn.iteration_count}
              {turn.tool_call_count > 0 && (
                <Text type="secondary">{t('insights.costTimeline.nToolCalls', { count: turn.tool_call_count })}</Text>
              )}
            </div>
          </div>
        </div>

        {/* Cost */}
        <div style={{ minWidth: 80, textAlign: 'end' }}>
          <Text strong style={{ fontSize: 14 }}>{fmtUsd(turn.total_cost_usd)}</Text>
        </div>
      </div>

      {/* Expanded: per-iteration events.
          The explanation is rendered as a plain inline sentence — no Alert,
          no heading, no icon — so the expand panel reads like a quick note
          followed by the iteration rows. Keeps vertical real estate tight. */}
      {expanded && (
        <div style={{ padding: '8px 16px 16px 60px', background: token.colorBgLayout }}>
          {turn.cache_explanation && (
            <Text
              type="secondary"
              style={{
                display: 'block',
                fontSize: 12,
                lineHeight: 1.45,
                marginBlockEnd: 8,
              }}
            >
              {turn.cache_explanation}
            </Text>
          )}
          {turn.events.length === 0 ? (
            <Empty description={t('insights.costTimeline.noEvents')} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {turn.events.map((ev, eidx) => (
                <EventRow key={ev.event_id} ev={ev} idx={eidx} />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

function EventRow({ ev }: { ev: ConversationTurnEvent; idx: number }) {
  const cacheMeta = ev.cache_state ? CACHE_STATE_META[ev.cache_state] : null;
  return (
    <div
      style={{
        padding: '6px 12px',
        background: 'rgba(0,0,0,0.02)',
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 12,
      }}
    >
      <Text type="secondary" style={{ minWidth: 28 }}>
        {ev.iteration_index != null ? `#${ev.iteration_index}` : '—'}
      </Text>
      <Tag>{ev.call_kind}</Tag>
      {ev.tool_name && <Tag color="blue">{ev.tool_name}</Tag>}
      <Text style={{ minWidth: 200 }}>{ev.provider}/{ev.model_name}</Text>
      {ev.input_tokens > 0 && (
        <Text type="secondary" style={{ minWidth: 60 }}>in {fmtN(ev.input_tokens)}</Text>
      )}
      {/* Embeddings legitimately have no output (they return a vector,
          not text), so hide "out 0" instead of rendering misleading noise.
          Same rule the other token columns already follow below. */}
      {ev.output_tokens > 0 && (
        <Text type="secondary" style={{ minWidth: 60 }}>out {fmtN(ev.output_tokens)}</Text>
      )}
      {ev.cache_read_tokens > 0 && (
        <Text style={{ color: '#10b981', minWidth: 80 }}>cache rd {fmtN(ev.cache_read_tokens)}</Text>
      )}
      {ev.cache_creation_tokens > 0 && (
        <Text style={{ color: '#f59e0b', minWidth: 80 }}>cache wr {fmtN(ev.cache_creation_tokens)}</Text>
      )}
      {ev.reasoning_tokens > 0 && (
        <Text style={{ color: '#a855f7', minWidth: 90 }}>reasoning {fmtN(ev.reasoning_tokens)}</Text>
      )}
      <Text strong style={{ marginInlineStart: 'auto' }}>{fmtUsd(ev.total_cost_usd)}</Text>
      {cacheMeta && <Tag color={cacheMeta.color} style={{ fontSize: 10 }} dir="auto">{cacheMeta.label}</Tag>}
    </div>
  );
}
